import React, { useEffect, useState } from "react";
import { Link, Outlet, NavLink, useLocation } from "react-router-dom";
import { Sparkles, LogIn, Home, Search, Layers, DollarSign, Megaphone, UserPlus, Menu, X } from "lucide-react";
import api from "@/api/client";

const MENU = [
  { to: "/", label: "Home", icon: Home, exact: true },
  { to: "/browse", label: "Browse", icon: Search },
  { to: "/features", label: "Features", icon: Layers },
  { to: "/pricing", label: "Pricing", icon: DollarSign },
  { to: "/advertise", label: "Advertise With Us", icon: Megaphone },
  { to: "/signup", label: "Sign Up", icon: UserPlus, cta: true },
  { to: "/login", label: "Login", icon: LogIn },
];

export default function PublicShell() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [branding, setBranding] = useState({
    site_name: "AllBusinessOnline",
    site_tagline: "Multi Business Platform",
    logo_url: "",
  });
  const { pathname } = useLocation();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/public/branding");
        setBranding((prev) => ({ ...prev, ...data }));
        if (data.site_name) {
          document.title = `${data.site_name}${data.site_tagline ? " — " + data.site_tagline : ""}`;
        }
      } catch { /* keep defaults */ }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex">
      {/* Left Sidebar (public) */}
      <aside
        data-testid="public-sidebar"
        className={`${
          mobileOpen ? "block" : "hidden"
        } lg:block fixed lg:sticky top-0 left-0 h-screen w-64 border-r border-slate-800 bg-slate-950 z-40 flex-shrink-0`}
      >
        <div className="p-6 border-b border-slate-800">
          <Link
            to="/"
            onClick={() => setMobileOpen(false)}
            className="flex items-center gap-2.5"
            data-testid="public-logo"
          >
            {branding.logo_url ? (
              <img
                src={branding.logo_url}
                alt={branding.site_name}
                className="w-full h-auto object-contain"
                data-testid="public-logo-img"
              />
            ) : (
              <span className="font-serif text-2xl font-medium text-sky-100 tracking-tight">
                {branding.site_name?.replace(/(Online|Platform)$/i, "").trim() || "AllBusiness"}
                <span className="text-sky-400">.</span>
              </span>
            )}
          </Link>
          {!branding.logo_url && (
            <p className="font-mono text-sm font-semibold uppercase tracking-[0.15em] text-sky-200 mt-2.5 leading-relaxed" data-testid="public-tagline">
              {branding.site_tagline || "Multi Business Platform"}
            </p>
          )}
        </div>

        <nav className="p-4 space-y-1">
          {MENU.map((item) => {
            const Icon = item.icon;
            const active = item.exact ? pathname === item.to : pathname.startsWith(item.to);
            const base = "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition";
            if (item.cta) {
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`public-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setMobileOpen(false)}
                  className={`${base} mt-4 bg-sky-400 hover:bg-sky-300 text-slate-950 font-medium`}
                >
                  <Icon className="w-4 h-4" /> {item.label}
                </Link>
              );
            }
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`public-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setMobileOpen(false)}
                end={item.exact}
                className={() =>
                  `${base} ${
                    active
                      ? "bg-sky-400/10 text-sky-200 border-l-2 border-sky-400 pl-[10px]"
                      : "text-slate-400 hover:text-sky-100 hover:bg-slate-900 border-l-2 border-transparent pl-[10px]"
                  }`
                }
              >
                <Icon className="w-4 h-4" strokeWidth={1.5} /> {item.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="mx-4 mt-6 p-4 rounded-lg border border-sky-500/25 bg-gradient-to-br from-sky-500/8 to-transparent">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-3.5 h-3.5 text-sky-400" />
            <p className="font-mono text-[10px] uppercase tracking-widest text-sky-300/80">Engine</p>
          </div>
          <p className="font-serif text-sm text-sky-100">GPT-5.6 Terra</p>
          <p className="text-[11px] text-slate-500 mt-1">Autonomous affiliate articles</p>
        </div>
      </aside>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-30 bg-black/60" onClick={() => setMobileOpen(false)} />
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <div className="lg:hidden sticky top-0 z-20 border-b border-slate-800 bg-slate-950/90 backdrop-blur px-4 py-3 flex items-center justify-between">
          <Link to="/" className="font-serif text-lg text-sky-100">
            AllBusiness<span className="text-sky-400">.</span>
          </Link>
          <button
            data-testid="public-mobile-menu"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="p-2 text-slate-400 hover:text-sky-300"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        <main className="flex-1"><Outlet /></main>

        <footer className="border-t border-slate-800 py-8 px-6">
          <div className="max-w-[1400px] mx-auto text-center">
            <p className="text-[10px] font-mono uppercase tracking-widest text-slate-600">
              © 2026 AllBusinessOnline · Multi Business Platform
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
}
